import { ref, watch, computed } from 'vue';
import { useAuthentication, useFirebaseDatabase, useAnalytics } from 'ihunter-pathfinder-components';
import { useCatalog } from './useCatalog';
import { PurchaseResources, isNumeric } from 'ihunter-common';
import { SubscriptionTier, SubscriptionSku } from './useConstants';

const PLATFORM = {
    Apple: 'ios',
    Android: 'android',
    Web: 'web',
};

const purchasedMaps = ref([]);
const purchasedSubscriptions = ref([]);
const purchasedProducts = ref({});
const purchasesInitialized = ref(false);

let watchersInitialized = false;

let keymap = {};
let items = {};
let purchases = [];
let regionsWithAllMaps = new Set();

let watching = false; // Ensure we only set up the watchers once

export function usePurchases() {
    const { catalog } = useCatalog();
    const { watchData } = useFirebaseDatabase();
    const { user } = useAuthentication();
    const { setAnalyticsIsFreeUser } = useAnalytics();

    const watchPurchases = async () => {
        if (!watchersInitialized && user.value && catalog.value) {
            reset();
            watchersInitialized = true;

            console.log(`Initializing purchase watchers for user ${user.value.uid}`);

            updateProductCatalog(catalog.value);

            await watchPlatform(PLATFORM.Apple, PurchaseResources.Apple(user.value.uid));
            await watchPlatform(PLATFORM.Android, PurchaseResources.Android(user.value.uid));
            await watchPlatform(PLATFORM.Web, PurchaseResources.Web(user.value.uid));
        }
    };

    if (!watching) {
        watching = true;
        watch(
            [user, catalog],
            () => {
                if (user.value && catalog.value) {
                    watchPurchases();
                }
            },
            { immediate: true },
        );
    }

    const reset = () => {
        keymap = {};
        items = {};
        purchases = [];
        regionsWithAllMaps.clear();

        purchasedMaps.value = [];
        purchasedSubscriptions.value = [];
        purchasedProducts.value = {};
        purchasesInitialized.value = false;
    };

    const updateProductCatalog = (catalog) => {
        for (const region in catalog) {
            updateProductCatalogForRegion(catalog[region], region);
        }
        console.log(`Subscription keymap for catalog`, keymap);
    };

    const updateProductCatalogForRegion = (catalog, region) => {
        const subscriptions = Object.values(catalog?.subscriptions || {});
        for (const subscription of subscriptions) {
            for (const key in PLATFORM) {
                const platform = PLATFORM[key];
                const sku = subscription[`sku_${platform}`];
                keymap[platform] = keymap[platform] || {};
                keymap[platform][region] = keymap[platform][region] || {};
                keymap[platform][region][sku] = { sku: subscription.sku_web, name: subscription.name };
            }
        }
    };

    const watchPlatform = async (platform, path) => {
        await watchData(path, async (data) => {
            try {
                items[platform] = data;
                processPurchases(platform, items[platform]);
                updateMapsAndSubscriptions();
            } catch (error) {
                console.error(`Updating ${platform} product purchases`, error);
            }
        });
    };

    const updateMapsAndSubscriptions = () => {
        purchasesInitialized.value = Object.keys(items).length === Object.keys(PLATFORM).length;
        if (purchasesInitialized.value) {
            if (regionsWithAllMaps.size > 0) {
                purchases = [...purchases, ...constructPurchasesForAllMaps()];
            }

            // Flatten purchases array into sku hash
            purchasedProducts.value = purchases.reduce((acc, item) => {
                acc[item.sku] = item;
                return acc;
            }, {});
            purchasedMaps.value = purchases.filter((x) => !x.isSubscription);
            purchasedSubscriptions.value = purchases.filter((x) => x.isSubscription).sort(sortSubscriptions);

            console.log(`purchasedProducts`, purchasedProducts.value);
            console.log(`purchasedMaps`, purchasedMaps.value);
            console.log(`purchasedSubscriptions`, purchasedSubscriptions.value);
            setAnalyticsIsFreeUser(isFreeInAllRegions.value);
        }
    };

    const constructPurchasesForAllMaps = () => {
        const allMapsPurchases = [];
        for (const region of regionsWithAllMaps) {
            const maps = catalog.value[region]?.resources?.purchasable_layers?.layers?.county?.maps || {};
            const mapPurchases = Object.values(maps)
                .map((x) => constructMapPurchase(x, region))
                .filter((x) => x?.sku);

            const subscriptions = catalog.value[region]?.subscriptions || {};
            const subscriptionPurchases = Object.values(subscriptions)
                .map((subscription) => constructSubscriptionPurchase(subscription, region))
                .filter((subscription) => subscription?.tier === SubscriptionTier.Pro);

            allMapsPurchases.push(...mapPurchases);
            allMapsPurchases.push(...subscriptionPurchases);
        }

        return allMapsPurchases;
    };

    const constructMapPurchase = (map, region) => {
        const skus = Object.keys(map.products || {});
        const sku = skus.sort().pop(); // Grant access to the latest sku
        if (sku) {
            return {
                id: `${SubscriptionSku.AllMaps}_${sku}`,
                sku,
                region,
                bounds: map.products[sku].bounds,
                name: map.name,
                platform: [PLATFORM.Web],
            };
        }
        return null;
    };

    const constructSubscriptionPurchase = (subscription, region) => {
        const sku = subscription?.sku_web;
        if (sku) {
            return {
                id: `${SubscriptionSku.AllMaps}_${sku}`,
                sku,
                name: subscription.name || sku,
                region: region,
                isSubscription: true,
                tier: getTierForSubscription({ sku }),
                platform: [PLATFORM.Web],
            };
        }
        return null;
    };

    const processPurchases = (platform, items) => {
        if (!items) {
            return;
        }

        for (let region in items) {
            for (let key in items[region]) {
                addPurchase(platform, region, key, items[region][key]);
            }
        }
    };

    const addPurchase = (platform, region, key, item) => {
        if (!keymap[platform] || !keymap[platform][region]) {
            return;
        }

        const sku = reconstructSku(key, region);
        const data = typeof item === 'string' ? { id: item } : item;

        if (isAllMapsSku(sku, region) && validatePurchase(data)) {
            regionsWithAllMaps.add(region);
            console.log(`All Maps is purchased for region ${region}`);
        } else if (!isAllMapsSku(sku, region)) {
            let subscription = keymap[platform]?.[region]?.[sku];
            const purchase = {
                ...data,
                region,
                platforms: [platform],
                sku: subscription?.sku || sku,
                name: subscription?.name || sku,
                isSubscription: Boolean(subscription),
                ...(subscription && { tier: getTierForSubscription(subscription) }),
            };

            if (validatePurchase(purchase)) {
                let p = purchases.find((x) => x.sku === purchase.sku && x.region === purchase.region);
                if (p) {
                    p.platforms = p.platforms.concat(purchase.platforms);
                } else {
                    purchases.push(purchase);
                }
            }
        }
    };

    const reconstructSku = (key) => {
        return key.replace(/,/g, '.');
    };

    const validatePurchase = (purchase) => {
        if (isNumeric(purchase.expirydate)) {
            const expiry = new Date(Number(purchase.expirydate) * 1000);
            if (expiry.getTime() > 0) {
                // If valid datetime number
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Grace period until midnight on the expiry date
                let expired = expiry.getTime() < today.getTime();
                return !expired;
            }
        }
        return true;
    };

    const sortSubscriptions = (a, b) => {
        if (a.region === b.region) {
            return a.name.localeCompare(b.name);
        }
        return a.region > b.region ? 1 : -1;
    };

    const getTierForSubscription = (subscription) => {
        if (subscription?.sku.endsWith(SubscriptionSku.Pro)) {
            return SubscriptionTier.Pro;
        } else if (subscription?.sku.endsWith(SubscriptionSku.Core)) {
            return SubscriptionTier.Core;
        } else if (subscription) {
            SubscriptionTier.Free;
        }
    };

    const getSubscriptionTierForRegion = (region) => {
        let tier = SubscriptionTier.Free;

        try {
            if (!region) {
                return SubscriptionTier.Free;
            }

            const subscriptions = purchasedSubscriptions.value.filter((x) => x.region === region);
            for (const subscription of subscriptions) {
                if (subscription.tier === SubscriptionTier.Pro) {
                    return SubscriptionTier.Pro; // return as Pro is the highest ranking subscription
                } else if (subscription.tier === SubscriptionTier.Core) {
                    tier = SubscriptionTier.Core;
                }
            }
        } catch (error) {
            console.error(`populateSubscriptionTiers`, error);
        }
        return tier;
    };

    const isAllMapsSku = (sku, region) => {
        return sku === SubscriptionSku.AllMaps || sku === `${region}_${SubscriptionSku.AllMaps}`;
    };

    const isFreeInAllRegions = computed(() => {
        return purchasedSubscriptions.value.length === 0;
    });

    return {
        purchasedMaps,
        purchasedSubscriptions,
        purchasedProducts,

        isFreeInAllRegions,
        purchasesInitialized,

        getSubscriptionTierForRegion,
        watchPurchases,
    };
}
